/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import chunk from "lodash/chunk";
import debounce from "lodash/debounce";
import { Modal } from "@/components/modal/modal";
import { Button } from "@/components/ui/button";
import { TextInput } from "ui/ecosystems/forms/inputs";
import { SuggestionsPopup } from "ui/suggestions-popup";
import CloseIcon from "@/components/modal/close-icon.svg";
import {
  fetchLocations,
  fetchOrganizationLabelById,
} from "api/linkedinTargetAudience";
import { lookupLinkedInOrganization } from "api/autocomplete";
import companySizeData from "constants/linkedinAudiences/companySize.json";
import jobFunctionsData from "constants/linkedinAudiences/jobFunctions.json";
import senioritiesData from "constants/linkedinAudiences/seniorities.json";
import industriesData from "constants/linkedinAudiences/industries.json";

export type ListItem = {
  id: string;
  label: string;
};

export type ListItemWithKey = ListItem & {
  fieldKey: string;
};

export type Audiences = {
  [key: string]: ListItem[];
};

type LinkedInOrganizationResult = {
  id: string;
  localizedName: string;
  primaryOrganizationType: string;
};

export interface TargetAudienceProps {
  showModal: boolean;
  onCloseModal: (selectedItem?: Audiences) => void;
  lists: ListItem[];
  selectedItems: Audiences;
}

export const generateAudienceLabel = (audiences: { string: ListItem[] }[]) => {
  return audiences
    .filter((audience) => Object.keys(audience)[0] !== "interfaceLocales")
    .map((audience) => {
      const audienceData = Object.values(audience)[0];

      return audienceData.map((item) => item.label);
    });
};

const LinkedinTargetAudience: React.FC<TargetAudienceProps> = ({
  showModal,
  onCloseModal,
  lists,
  selectedItems,
}) => {
  const [selectedItemToEdit, setSelectedItemToEdit] = React.useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [suggestionList, setSuggestionList] = useState<ListItemWithKey[]>([]);
  const [selectedSuggestionItem, setSelectedSuggestionItem] =
    useState<Audiences>(selectedItems ? selectedItems : {});

  const handleAddItem = (id: string) => () => {
    setSelectedItemToEdit(id);
  };

  const debouncedSearch = useCallback(
    debounce(async (selectedItemToEdit, searchTerm) => {
      if (selectedItemToEdit === "geoLocations") {
        fetchLocations(selectedItemToEdit, searchTerm).then((data) => {
          setSuggestionList(data);
        });
      } else if (selectedItemToEdit === "organizations") {
        lookupLinkedInOrganization(searchTerm).then((data) => {
          if (data && data.urn) {
            fetchOrganizationLabelById(
              data.urn.replace("urn:li:organization:", "")
            )
              .then((data: any) => {
                return (
                  Object.entries(
                    data.results as Record<string, LinkedInOrganizationResult>
                  )
                    // For some reason, primaryOrganizationType filed of schools is NONE
                    .filter(([, val]) => {
                      const organization = val as LinkedInOrganizationResult;
                      return (
                        organization.primaryOrganizationType === "SCHOOL" ||
                        organization.primaryOrganizationType === "NONE"
                      );
                    })
                    .map(([, val]) => {
                      const organization = val as LinkedInOrganizationResult;
                      return {
                        fieldKey: selectedItemToEdit,
                        id: `${organization.id}`.includes("urn")
                          ? organization.id
                          : `urn:li:organization:${organization.id}`,
                        label: organization.localizedName,
                      };
                    }) as ListItemWithKey[]
                );
              })
              .then((data) => setSuggestionList(data));
          }
        });
      } else if (selectedItemToEdit === "staffCountRanges") {
        setSuggestionList(
          companySizeData
            .map((item: ListItem) => ({
              ...item,
              fieldKey: selectedItemToEdit,
            }))
            .filter((item) => item.label.toLowerCase().includes(searchTerm))
        );
      } else if (selectedItemToEdit === "seniorities") {
        setSuggestionList(
          senioritiesData
            .map((item: ListItem) => ({
              ...item,
              fieldKey: selectedItemToEdit,
            }))
            .filter((item) => item.label.toLowerCase().includes(searchTerm))
        );
      } else if (selectedItemToEdit === "jobFunctions") {
        setSuggestionList(
          jobFunctionsData
            .map((item: ListItem) => ({
              ...item,
              fieldKey: selectedItemToEdit,
            }))
            .filter((item) => item.label.toLowerCase().includes(searchTerm))
        );
      } else if (selectedItemToEdit === "industries") {
        setSuggestionList(
          industriesData
            .map((item: ListItem) => ({
              ...item,
              fieldKey: selectedItemToEdit,
            }))
            .filter((item) => item.label.toLowerCase().includes(searchTerm))
        );
      }
    }, 500),
    []
  );

  useEffect(() => {
    const searchTermTrimmedAndLowerCased = searchTerm.trim().toLowerCase();

    if (searchTermTrimmedAndLowerCased.length > 0) {
      setSuggestionList([]);
      debouncedSearch(selectedItemToEdit, searchTermTrimmedAndLowerCased);
    }
  }, [debouncedSearch, selectedItemToEdit, searchTerm]);

  const handleInputChange = (value: string) => setSearchTerm(value);
  const handleInputBlur = () => {
    if (searchTerm.length === 0) {
      setSelectedItemToEdit("");
      setSuggestionList([]);
    }
  };
  const handleOnSelectSuggestion = (suggestion: ListItemWithKey) => {
    const itemExists = selectedSuggestionItem[suggestion.fieldKey]?.find(
      (item) => item.id === suggestion.id
    );

    if (!itemExists) {
      setSelectedSuggestionItem({
        ...selectedSuggestionItem,
        [suggestion.fieldKey]: [
          ...(selectedSuggestionItem[suggestion.fieldKey] || []),
          suggestion,
        ],
      });
    }

    setSelectedItemToEdit("");
    setSearchTerm("");
  };

  const handleRemoveSuggestionItem = (itemId: string) => () => {
    const selectedSuggestionItemKeys = Object.keys(selectedSuggestionItem);
    const nextSelectedSuggestionItem = selectedSuggestionItemKeys.reduce(
      (acum: Audiences, key: string) => {
        const items = selectedSuggestionItem[key];

        if (key === "interfaceLocales") return acum;

        const nextItems = items.filter((item) => item.id !== itemId);

        return { ...acum, [key]: nextItems };
      },
      {}
    );

    setSelectedSuggestionItem(nextSelectedSuggestionItem);
  };

  const handleSave = (selectedItem?: Audiences) => () => {
    onCloseModal(selectedItem);
  };

  const filteredSuggestionList = useMemo(
    () =>
      suggestionList.filter((suggestion: ListItem) => {
        const selectedSuggestionItemValuesIds = Object.entries(
          selectedSuggestionItem
        ).reduce((acum: string[], [key, items]) => {
          if (key === "interfaceLocales") return acum;

          acum = [...acum, ...items.map((item) => item.id)];

          return acum;
        }, []);

        return !selectedSuggestionItemValuesIds.includes(suggestion.id);
      }),
    [suggestionList, selectedSuggestionItem]
  );

  const audienceColumns = useMemo(() => {
    const columnCount = 2;
    const itemsPerColumn = Math.max(1, Math.ceil(lists.length / columnCount));

    return chunk(lists, itemsPerColumn) as (typeof lists)[];
  }, [lists]);

  return (
    <StyledModal show={showModal} onHide={onCloseModal}>
      <Modal.Header closeButton onHide={() => onCloseModal(undefined)}>
        <HeaderContent>
          <Modal.Title>Target audience setting</Modal.Title>
          <AudienceNote>
            Please note that LinkedIn requires a target audience greater than
            300 or the post will fail.{" "}
            <AudienceNoteLink
              href="https://www.linkedin.com/help/lms/answer/a424655"
              target="_blank"
              rel="noopener noreferrer"
            >
              Learn more
            </AudienceNoteLink>
          </AudienceNote>
        </HeaderContent>
      </Modal.Header>

      {Object.keys(selectedSuggestionItem).length > 0 && (
        <SelectedSuggestionItemWrap>
          {Object.entries(selectedSuggestionItem)
            .filter(([key]) => key !== "interfaceLocales")
            .map(([, items]) =>
              items.map((item) => (
                <div key={item.id}>
                  <div className="flex items-center gap-[10px] rounded-[50px] border p-[10px] text-[12px] font-medium">
                    {item.label}
                    <CloseIconButton
                      onClick={handleRemoveSuggestionItem(item.id)}
                    >
                      <CloseIcon />
                    </CloseIconButton>
                  </div>
                </div>
              ))
            )}
        </SelectedSuggestionItemWrap>
      )}

      <Wrap>
        {audienceColumns.map((list) => (
          <Column key={list[0].id}>
            {list.map(({ id, label }) => (
              <Item key={id}>
                <ItemLabel>{label}</ItemLabel>
                {selectedItemToEdit !== id && (
                  <AddLabel onClick={handleAddItem(id)}>
                    + Add <span>{label}</span>
                  </AddLabel>
                )}
                {selectedItemToEdit === id && (
                  <TextInputStyled
                    placeholder={label}
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    autoFocus
                  />
                )}
                {selectedItemToEdit === id &&
                  searchTerm.length > 0 &&
                  filteredSuggestionList &&
                  filteredSuggestionList.length > 0 && (
                    <div className="absolute left-0 right-0 top-[calc(100%+4px)] z-[5]">
                      <SuggestionsPopup
                        className="rounded-[5px] shadow-[0_5px_15px_rgb(0_0_0_/_12%)] [&_.simplebar-content>div]:p-2"
                        suggestions={filteredSuggestionList}
                        onSelect={handleOnSelectSuggestion}
                        renderSuggestion={({
                          suggestion,
                        }: {
                          suggestion: any;
                        }) => <div>{suggestion.label as string}</div>}
                      ></SuggestionsPopup>
                    </div>
                  )}
              </Item>
            ))}
          </Column>
        ))}
      </Wrap>

      <Modal.Footer className="mt-2 justify-end [&_button]:min-w-[100px] [&_button]:text-[14px]">
        <Button
          data-testid="saveAudience"
          variant="blue-primary"
          size="lg"
          onClick={handleSave(selectedSuggestionItem)}
        >
          <span>Save</span>
        </Button>
      </Modal.Footer>
    </StyledModal>
  );
};

const Wrap = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(220px, 1fr));
  gap: 12px 36px;
  margin-top: 4px;
  align-items: start;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const AudienceNote = styled.p`
  margin: 0;
  color: #5f6368;
  font-size: 13px;
  line-height: 1.4;
`;

const AudienceNoteLink = styled.a`
  color: #4f31ff;

  &:hover {
    text-decoration: underline;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
`;

const Item = styled.div`
  position: relative;
  min-height: 54px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemLabel = styled.div`
  color: #3a3a3a;
  font-weight: 400;
  line-height: 1.25;
`;

const AddLabel = styled.div`
  cursor: pointer;
  color: #4f31ff;
  font-weight: 500;
  margin-left: 4px;
  line-height: 1.25;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover {
    text-decoration: underline;
  }

  span {
    text-transform: lowercase;
  }
`;

const TextInputStyled = styled(TextInput)`
  padding: 8px;
  font-size: 14px;
`;

const StyledModal = styled(Modal)`
  width: min(760px, 92vw);
  max-width: 760px;
  min-width: 0;
`;

const CloseIconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  svg {
    width: 10px;
    height: 10px;
  }
`;

const SelectedSuggestionItemWrap = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 12px;
  padding: 4px 0 2px;
  border-bottom: 1px solid #ddd;
`;

export default LinkedinTargetAudience;
